import { useEffect, useState } from 'react';
import type { OasisRecordCheckResponse } from '../services/api/types.gen';

/** Quiet time after the last keystroke before the values are judged. */
export const CMS_RECORD_CHECK_DELAY_MS = 500;

/**
 * Judges a record's values against the OASIS export's CMS rules as they are
 * typed. `body` null means not to judge (no edit grant, dialog
 * closed): the result is then empty. A failed check shows nothing rather
 * than an error: it only advises, and the export judges again.
 */
export function useCmsRecordCheck<T>(
  body: T | null,
  check: (body: T) => Promise<OasisRecordCheckResponse | undefined>
): OasisRecordCheckResponse | null {
  const [result, setResult] = useState<OasisRecordCheckResponse | null>(null);
  const key = body === null ? null : JSON.stringify(body);
  useEffect(() => {
    if (key === null) {
      setResult(null);
      return;
    }
    let current = true;
    const timer = setTimeout(() => {
      check(JSON.parse(key) as T)
        .then(data => current && setResult(data ?? null))
        .catch((error: unknown) => {
          console.error('CMS record check failed:', error);
          if (current) setResult(null);
        });
    }, CMS_RECORD_CHECK_DELAY_MS);
    return () => {
      current = false;
      clearTimeout(timer);
    };
    // `check` is a call to one endpoint; `key` is what changes the answer.
  }, [key]);
  return result;
}
